import React from "react";
import { ApexOptions } from "apexcharts";
import Chart from "react-apexcharts";
import CountUp from "react-countup";
import { motion } from "framer-motion";
import { ChevronDown, Info } from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import { cn } from "lib/utils";

interface StatCardData {
  title: string;
  value: number | string;
  /** Token-based styling (new). */
  bg?: string;
  accent?: string;
  border?: string;
  /** Legacy aliases. */
  bgColor?: string;
  textColor?: string;
  borderColor?: string;
  icon: string;
  isPercentage?: boolean;
}

interface ChartLegendItem {
  color: string;
  label: string;
  value: string;
}

interface SummaryStatItem {
  color: string;
  label: string;
  value: number;
}

interface AppointmentsOverviewProps {
  title?: string;
  chartOptions?: ApexOptions;
  chartSeries?: number[];
  statCards?: StatCardData[];
  chartLegends?: ChartLegendItem[];
  barChartSeries?: any[];
  barChartOptions?: ApexOptions;
  summaryStats?: SummaryStatItem[];
}

const SelectShell: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = ({
  className,
  children,
  ...props
}) => (
  <div className="relative">
    <select
      className={cn(
        "h-9 cursor-pointer appearance-none rounded-md border border-border bg-background px-3 pr-8 text-xs font-medium text-foreground transition-colors hover:border-border/80 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1",
        className
      )}
      {...props}
    >
      {children}
    </select>
    <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
  </div>
);

const AppointmentsOverview: React.FC<AppointmentsOverviewProps> = ({
  title = "Appointments Overview",
  chartOptions,
  chartSeries,
  statCards,
  chartLegends,
  barChartSeries,
  barChartOptions,
  summaryStats,
}) => {
  const hasDonutData =
    chartSeries && chartSeries.length > 0 && chartSeries.some((v) => v > 0);

  const resolveStatStyles = (card: StatCardData) => ({
    bg: card.bg ?? card.bgColor,
    border: card.border ?? card.borderColor,
    accent: card.accent ?? card.textColor,
  });

  return (
    <Card className="overflow-hidden">
      <CardHeader className="border-b border-border/60">
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="flex flex-col gap-6 xl:flex-row">
          {/* Left: stats + donut */}
          <div className="min-w-0 flex-1">
            {/* Stat cards */}
            <div className="mb-6 space-y-3">
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                {statCards?.slice(0, 2).map((card, index) => {
                  const s = resolveStatStyles(card);
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.06 }}
                      className={cn(
                        "rounded-xl border p-4",
                        s.bg,
                        s.border
                      )}
                    >
                      <div className="mb-1.5 flex items-center gap-2">
                        <span className="text-base sm:text-lg">{card.icon}</span>
                        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                          {card.title}
                        </span>
                      </div>
                      <div className={cn("text-2xl font-semibold tracking-tight sm:text-3xl", s.accent)}>
                        {card.isPercentage ? (
                          card.value
                        ) : (
                          <CountUp
                            end={typeof card.value === "number" ? card.value : 0}
                            duration={1.6}
                          />
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              <div className="grid grid-cols-3 gap-3 sm:gap-4">
                {statCards?.slice(2).map((card, index) => {
                  const s = resolveStatStyles(card);
                  return (
                    <motion.div
                      key={index + 2}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: (index + 2) * 0.06 }}
                      className={cn("rounded-xl border p-3 sm:p-4", s.bg, s.border)}
                    >
                      <div className="mb-1 flex items-center gap-1.5">
                        <span className="text-sm sm:text-base">{card.icon}</span>
                        <span className="truncate text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                          {card.title}
                        </span>
                      </div>
                      <div className={cn("text-lg font-semibold tracking-tight sm:text-2xl", s.accent)}>
                        {card.isPercentage ? (
                          card.value
                        ) : (
                          <CountUp
                            end={typeof card.value === "number" ? card.value : 0}
                            duration={1.6}
                          />
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            <h3 className="mb-4 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              AI vs Staff Scheduled
            </h3>

            <div className="flex flex-col items-center">
              {hasDonutData ? (
                <>
                  <Chart
                    options={{
                      ...chartOptions,
                      chart: {
                        ...chartOptions?.chart,
                        height: window.innerWidth < 640 ? 200 : 280,
                      },
                    }}
                    series={chartSeries}
                    type="donut"
                    height={window.innerWidth < 640 ? 200 : 280}
                  />
                  <div className="mt-4 flex items-center justify-center gap-3">
                    {chartLegends?.map((legend, index) => (
                      <div key={index} className="flex items-center">
                        <span
                          className={cn(
                            "mr-2 h-2.5 w-2.5 rounded-full",
                            legend.color
                          )}
                        />
                        <span
                          className={cn(
                            "text-xs sm:text-sm",
                            legend.label
                          )}
                        >
                          {legend.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="flex h-56 items-center justify-center">
                  <div className="text-center">
                    <Info className="mx-auto mb-3 h-12 w-12 text-muted-foreground/60" />
                    <p className="text-sm font-medium text-muted-foreground sm:text-base">
                      No Data Available
                    </p>
                    <p className="text-xs text-muted-foreground/80 sm:text-sm">
                      There are no appointments scheduled to display
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right: bar chart */}
          <div className="min-w-0 flex-1 xl:flex-[2]">
            <div className="mb-4 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
              <h3 className="text-sm font-semibold text-foreground">
                Appointments by Day
              </h3>
              <div className="flex flex-col gap-2 sm:flex-row">
                <SelectShell>
                  <option>This Week</option>
                  <option>Last Week</option>
                  <option>This Month</option>
                </SelectShell>
                <SelectShell>
                  <option>All Campaigns</option>
                  <option>Campaign 1</option>
                  <option>Campaign 2</option>
                </SelectShell>
              </div>
            </div>

            <div className="rounded-xl border border-border/60 p-2 sm:p-4">
              <Chart
                options={{
                  ...barChartOptions,
                  chart: {
                    ...barChartOptions?.chart,
                    height: window.innerWidth < 640 ? 200 : 280,
                  },
                }}
                series={barChartSeries}
                type="bar"
                height={window.innerWidth < 640 ? 200 : 280}
              />
            </div>

            <div className="mt-4 border-t border-border/60 pt-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                {summaryStats?.map((stat, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span
                      className={cn("h-2 w-2 rounded-full", stat.color)}
                    />
                    <span className="text-xs text-muted-foreground">
                      {stat.label}:
                    </span>
                    <span className="text-xs font-semibold text-foreground">
                      <CountUp end={stat.value} duration={1.6} separator="," />
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AppointmentsOverview;
